import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import * as XLSX from 'xlsx';
import axios from '../api/axios';

const AdminAccountingDetailPage = () => {
  const { semesterId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  
  // State
  const [semester, setSemester] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [newTx, setNewTx] = useState({ 
    type: 'expense', 
    date: new Date().toISOString().split('T')[0], 
    item: '', 
    amount: '', 
    eventTitle: '', 
    receiptUrl: '' 
  });
  const [viewReceiptUrl, setViewReceiptUrl] = useState('');
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState('');
  const [showReceiptTip, setShowReceiptTip] = useState(false);
  const [dateSortOrder, setDateSortOrder] = useState('desc'); // 'asc' or 'desc'
  
  // File input ref for resetting after transaction is added
  const receiptFileInputRef = useRef(null);
  
  // Mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Load semester first, then transactions
  useEffect(() => {
    loadSemester();
  }, [semesterId]);

  // Load transactions when semester is loaded
  useEffect(() => {
    if (semester) {
      loadTransactions();
    }
  }, [semester]);
  
  const loadSemester = () => {
    // Find semester from all branches
    const branches = ['NCCU', 'NTU', 'TAIPEI'];
    for (const branch of branches) {
      const storedSemesters = localStorage.getItem(`semesters_${branch}`);
      if (storedSemesters) {
        const semesters = JSON.parse(storedSemesters);
        const found = semesters.find(s => s.id === Number(semesterId));
        if (found) {
          setSemester(found);
          return;
        }
      }
    }
    // If not found, redirect back
    navigate('/admin/accounting');
  };
  
  const loadTransactions = async () => {
    if (!semester) return;
    
    try {
      // Load from backend API
      const response = await axios.get(`/api/admin/finance?branch=${semester.branch}`);
      const allTransactions = Array.isArray(response.data) ? response.data : [];
      
      // Filter transactions by semester (using date range or store semester info in transaction)
      // For now, we'll load all transactions for the branch and filter client-side
      // TODO: Add semester field to FinanceTransaction entity for better filtering
      setTransactions(allTransactions);
      
      // Also update localStorage as backup
      localStorage.setItem(`transactions_${semesterId}`, JSON.stringify(allTransactions));
    } catch (error) {
      // Fallback to localStorage
      const storedTx = localStorage.getItem(`transactions_${semesterId}`);
      if (storedTx) {
        setTransactions(JSON.parse(storedTx));
      } else {
        setTransactions([]);
      }
    }
  };
  
  // Sorted transactions by date
  const sortedTransactions = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateSortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    });
    return sorted;
  }, [transactions, dateSortOrder]);

  // Calculate stats
  const stats = useMemo(() => {
    const revenue = transactions.filter(t => t.type === 'revenue').reduce((sum, t) => sum + Number(t.amount || 0), 0);
    const expenses = transactions.filter(t => t.type === 'expense').reduce((sum, t) => sum + Number(t.amount || 0), 0);
    return {
      revenue,
      expenses,
      netProfit: revenue - expenses
    };
  }, [transactions]);
  
  // Check if user is GA team member
  const isGATeamMember = (currentUser) => {
    if (!currentUser?.departments) return false;
    return currentUser.departments.some(dept => 
      dept.name === 'General Affairs Team' || 
      dept.name === 'GA' || 
      dept.name === 'General Affairs'
    );
  };
  
  // Add new transaction
  const handleAddTransaction = async (e) => {
    e.preventDefault();
    
    if (!semester) {
      alert('Semester information is missing.');
      return;
    }
    
    const { type, date, item, amount } = newTx;
    const trimmedItem = String(item || '').trim();
    const parsedAmount = Number(amount);
    if (!date || !trimmedItem || Number.isNaN(parsedAmount)) {
      alert('Please enter date, item, and amount. Amount must be a number.');
      return;
    }

    try {
      // Save to backend API
      const transactionData = {
        branchName: semester.branch,
        type,
        date,
        item: trimmedItem,
        amount: parsedAmount,
        eventTitle: newTx.eventTitle || '',
        receiptUrl: newTx.receiptUrl || '',
        submittedBy: user?.name || 'Unknown',
        status: type === 'expense' ? 'Pending' : 'Confirmed'
      };

      const response = await axios.post('/api/admin/finance', transactionData);
      const savedTransaction = response.data;
      
      // Update local state
      const updatedTransactions = [savedTransaction, ...transactions];
      setTransactions(updatedTransactions);
      
      // Also update localStorage as backup
      localStorage.setItem(`transactions_${semesterId}`, JSON.stringify(updatedTransactions));
      
      // Reset form
      setNewTx({ type: 'expense', date: new Date().toISOString().split('T')[0], item: '', amount: '', eventTitle: '', receiptUrl: '' });
      setReceiptPreviewUrl('');
      // Reset file input field
      if (receiptFileInputRef.current) {
        receiptFileInputRef.current.value = '';
      }
      
      alert('Transaction added successfully!');
    } catch (error) {
      alert('Failed to add transaction: ' + (error.response?.data?.error || error.message));
    }
  };
  
  // Mark expense as paid
  const handleMarkExpensePaid = async (txId) => {
    try {
      // Update in backend
      await axios.put(`/api/admin/finance/${txId}`, { status: 'Reimbursed' });
      
      // Update local state
      const updatedTransactions = transactions.map(tx => 
        tx.id === txId ? { ...tx, status: 'Reimbursed' } : tx
      );
      setTransactions(updatedTransactions);
      localStorage.setItem(`transactions_${semesterId}`, JSON.stringify(updatedTransactions));
      
      alert('Expense marked as paid successfully!');
    } catch (error) {
      alert('Failed to update expense status: ' + (error.response?.data?.error || error.message));
    }
  };
  
  // Delete transaction
  const handleDeleteTransaction = async (txId) => {
    if (!window.confirm('Are you sure you want to delete this transaction?')) return;
    
    try {
      // Delete from backend
      await axios.delete(`/api/admin/finance/${txId}`);
      
      // Update local state
      const updatedTransactions = transactions.filter(tx => tx.id !== txId);
      setTransactions(updatedTransactions);
      localStorage.setItem(`transactions_${semesterId}`, JSON.stringify(updatedTransactions));
      
      alert('Transaction deleted successfully!');
    } catch (error) {
      alert('Failed to delete transaction: ' + (error.response?.data?.error || error.message));
    }
  };
  
  // Export accounting data to XLSX
  const handleExportXLSX = () => {
    if (!semester) return;
    
    const data = transactions.map(tx => ({
      'Type': tx.type === 'expense' ? 'Expense' : 'Revenue',
      'Date': tx.date || '',
      'Event': tx.eventTitle || '—',
      'Item': tx.item || '',
      'Amount (NTD)': Number(tx.amount || 0),
      'Submitted By': tx.submittedBy || 'System',
      'Status': tx.status || 'Confirmed',
      'Receipt URL': tx.receiptUrl || ''
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    XLSX.writeFile(wb, `${semester.branch}_${semester.name}_Accounting.xlsx`);
  };

  if (!semester) {
    return (
      <div className="min-h-screen bg-gray-100 p-4 sm:p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Receipt Modal */}
        {viewReceiptUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4" onClick={() => setViewReceiptUrl('')}>
            <div className="bg-white p-4 rounded-lg shadow-2xl max-w-3xl w-full" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold">Receipt</h3>
                <button onClick={() => setViewReceiptUrl('')} className="text-gray-600 hover:text-gray-900">✕</button>
              </div>
              <div className="max-h-[75vh] overflow-auto">
                <img src={viewReceiptUrl} alt="Receipt" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        )}

        {/* Receipt Tip Modal */}
        {showReceiptTip && (
          <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4" onClick={() => setShowReceiptTip(false)}>
            <div className="bg-white rounded-lg max-w-md w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-start mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💡</span>
                  <h3 className="text-lg font-semibold text-gray-800">Receipt Upload Tips</h3>
                </div>
                <button 
                  onClick={() => setShowReceiptTip(false)} 
                  className="text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
                >
                  ✕
                </button>
              </div>
              <div className="text-sm text-gray-700 leading-relaxed">
                <p className="mb-2">
                  Please take a photo of receipts <strong>grouped by event</strong>.
                </p>
                <p>
                  Only receipts with a <strong>unified number (統編)</strong> are needed.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Back Button & Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/admin/accounting')}
            className="mb-4 text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
          >
            ← Back to Semesters
          </button>
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2">
            💰 {semester.name} - {semester.branch}
          </h1>
          <p className="text-gray-600 text-sm sm:text-base">Manage transactions for this semester</p>
        </div>

        {/* Semester Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <div className="bg-green-100 p-4 sm:p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-green-800">Total Revenue</h3>
            <p className="text-2xl sm:text-3xl font-bold">{stats.revenue.toLocaleString()} NTD</p>
          </div>
          <div className="bg-red-100 p-4 sm:p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-red-800">Total Expenses</h3>
            <p className="text-2xl sm:text-3xl font-bold">{stats.expenses.toLocaleString()} NTD</p>
          </div>
          <div className="bg-blue-100 p-4 sm:p-6 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800">Net Profit</h3>
            <p className="text-2xl sm:text-3xl font-bold">{stats.netProfit.toLocaleString()} NTD</p>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h2 className="text-xl font-semibold">Transactions ({transactions.length})</h2>
            <button 
              onClick={handleExportXLSX}
              className="bg-emerald-500 text-white px-4 py-2 rounded-lg hover:bg-emerald-600 font-medium text-sm sm:text-base w-full sm:w-auto"
            >
              📊 Export XLSX
            </button>
          </div>

          {transactions.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Transactions Yet</h3>
              <p className="text-gray-500">Add your first transaction using the form below</p>
            </div>
          ) : isMobile ? (
            /* Mobile Card View */
            <div className="space-y-4">
              {sortedTransactions.map(tx => (
                <div key={tx.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded ${tx.type === 'expense' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                          {tx.type === 'expense' ? '💸 Expense' : '💰 Revenue'}
                        </span>
                        {tx.type === 'expense' && (
                          <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded ${tx.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                            {tx.status === 'Pending' ? '⏳ Pending' : '✅ Reimbursed'}
                          </span>
                        )}
                      </div>
                      <p className="text-lg font-bold text-gray-900 mb-2">{Number(tx.amount).toLocaleString()} NTD</p>
                      <p className="text-sm text-gray-600 mb-1"><strong>Date:</strong> {new Date(tx.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</p>
                      {tx.eventTitle && <p className="text-sm text-gray-600 mb-1"><strong>Event:</strong> {tx.eventTitle}</p>}
                      <p className="text-sm text-gray-600 mb-1"><strong>Item:</strong> {tx.item}</p>
                      <p className="text-sm text-gray-500 mb-2"><strong>Submitted by:</strong> {tx.submittedBy || 'System'}</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                    {tx.receiptUrl && (
                      <a href={tx.receiptUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-900 text-sm font-medium px-2 py-1 bg-blue-50 rounded">
                        📄 Receipt
                      </a>
                    )}
                    {tx.type === 'expense' && tx.status === 'Pending' && (user?.role === 'ADMIN' || user?.role === 'PRESIDENT' || (user?.role === 'STAFF' && isGATeamMember(user))) && (
                      <button onClick={() => handleMarkExpensePaid(tx.id)} className="text-green-600 hover:text-green-900 text-sm font-medium px-2 py-1 bg-green-50 rounded">
                        Mark as Paid
                      </button>
                    )}
                    {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                      <button onClick={() => handleDeleteTransaction(tx.id)} className="text-red-600 hover:text-red-900 text-sm font-medium px-2 py-1 bg-red-50 rounded">
                        Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th 
                      className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                      onClick={() => setDateSortOrder(dateSortOrder === 'asc' ? 'desc' : 'asc')}
                    >
                      <div className="flex items-center gap-1">
                        DATE
                        <span className="text-gray-400">
                          {dateSortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      </div>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">TYPE</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">EVENT</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ITEM</th>
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">AMOUNT</th>
                    {/* <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">SUBMITTED BY</th> */}
                    <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">ACTIONS</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sortedTransactions.map(tx => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(tx.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded ${tx.type === 'expense' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                            {tx.type === 'expense' ? '💸 Expense' : '💰 Revenue'}
                          </span>
                          {tx.type === 'expense' && (
                            <span className={`px-3 py-1 inline-flex text-xs font-semibold rounded ${tx.status === 'Pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                              {tx.status === 'Pending' ? '⏳ Pending' : '✅ Reimbursed'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{tx.eventTitle || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{tx.item}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-gray-900">{Number(tx.amount).toLocaleString()} NTD</td>
                      {/* <td className="px-4 py-3 text-sm text-gray-600">{tx.submittedBy || 'System'}</td> */}
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {tx.receiptUrl && (
                            <a href={tx.receiptUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                              📄 Receipt
                            </a>
                          )}
                          {tx.type === 'expense' && tx.status === 'Pending' && (user?.role === 'ADMIN' || user?.role === 'PRESIDENT' || (user?.role === 'STAFF' && isGATeamMember(user))) && (
                            <button onClick={() => handleMarkExpensePaid(tx.id)} className="text-green-600 hover:text-green-900 text-sm font-medium">
                              Mark as Paid
                            </button>
                          )}
                          {(user?.role === 'LEADER' || user?.role === 'PRESIDENT' || user?.role === 'ADMIN') && (
                            <button onClick={() => handleDeleteTransaction(tx.id)} className="text-red-600 hover:text-red-900 text-sm font-medium">
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 text-center sm:text-right">
            <span className="text-sm text-gray-600 mr-2">Net Total ({semester.name}):</span>
            <span className={`text-lg font-bold ${stats.netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.netProfit.toLocaleString()} NTD
            </span>
          </div>

          {/* Add New Transaction Form */}
          <div className="mt-6 border-t pt-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="font-semibold text-base sm:text-lg">Add New Transaction</h3>
              <button
                type="button"
                onClick={() => setShowReceiptTip(true)}
                className="text-lg cursor-pointer hover:opacity-70 transition-opacity focus:outline-none"
                title="Receipt upload tips"
              >
                💡
              </button>
            </div>
            <form onSubmit={handleAddTransaction} className="space-y-4">
              {/* First row: Type, Date, Event, Item, Amount */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <select value={newTx.type} onChange={e => setNewTx(v => ({ ...v, type: e.target.value }))} className="p-2 border rounded text-sm sm:text-base">
                  <option value="revenue">Revenue</option>
                  <option value="expense">Expense</option>
                </select>
                <input type="date" value={newTx.date} onChange={e => setNewTx(v => ({ ...v, date: e.target.value }))} className="p-2 border rounded text-sm sm:text-base"/>
                <input type="text" placeholder="Event Name (optional)" value={newTx.eventTitle} onChange={e => setNewTx(v => ({ ...v, eventTitle: e.target.value }))} className="p-2 border rounded text-sm sm:text-base"/>
                <input type="text" placeholder="Item *" value={newTx.item} onChange={e => setNewTx(v => ({ ...v, item: e.target.value }))} className="p-2 border rounded text-sm sm:text-base"/>
                <input type="number" min="0" placeholder="Amount *" value={newTx.amount} onChange={e => setNewTx(v => ({ ...v, amount: e.target.value }))} className="p-2 border rounded text-sm sm:text-base"/>
              </div>
              
              {/* Second row: Receipt and Add button aligned to right */}
              <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end justify-end">
                <div className="flex flex-col flex-1 sm:flex-none">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                    <label className="text-sm text-gray-600 whitespace-nowrap">Receipt (≤5MB):</label>
                    <input
                      ref={receiptFileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          alert('Receipt size must be 5MB or less.');
                          e.target.value = '';
                          return;
                        }
                        const form = new FormData();
                        form.append('file', file);
                        axios.post('/api/admin/receipts/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } })
                          .then(res => {
                            const url = res?.data?.url;
                            if (url) {
                              setNewTx(prev => ({ ...prev, receiptUrl: url }));
                              setReceiptPreviewUrl('');
                              alert('Receipt uploaded successfully.');
                            } else {
                              alert('Upload response invalid.');
                            }
                          })
                          .catch(err => {
                            alert('Failed to upload receipt.');
                          });
                      }}
                      className="text-sm flex-1 sm:flex-none"
                    />
                  </div>
                </div>
                {receiptPreviewUrl && (
                  <button type="button" onClick={() => setViewReceiptUrl(receiptPreviewUrl)} className="text-xs text-blue-600 underline px-2 py-1">
                    Preview
                  </button>
                )}
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm sm:text-base font-medium w-full sm:w-auto">Add</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccountingDetailPage;

